<template>
  <div class="categories-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Categories</h1>
        <p>Manage categories used across courses, projects, blogs and content.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add Category
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-card">

      <div class="search-box">
        <i class="bx bx-search"></i>

        <input
          v-model="search"
          type="text"
          placeholder="Search categories..."
        />
      </div>

      <select v-model="selectedType">
        <option value="">All Types</option>

        <option
          v-for="type in categoryTypes"
          :key="type"
          :value="type"
        >
          {{ type }}
        </option>
      </select>

      <button
        class="reset-btn"
        @click="resetFilters"
      >
        <i class="bx bx-reset"></i>
        Reset
      </button>

    </div>

    <!-- Table -->
    <div class="table-card">

      <div class="table-top">
        <div>
          <h3>Category List</h3>
          <span>
            {{ filteredCategories.length }} categories
          </span>
        </div>
      </div>

      <div class="table-wrapper">

        <table>

          <thead>
            <tr>
              <th>Category</th>
              <th>Type</th>
              <th>Meta Title</th>
              <th>Meta Keywords</th>
              <th>Created</th>
              <th class="action-col">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr
  v-for="category in categories"
  :key="category.cat_id"
>

              <!-- Category -->
              <td>
                <div class="category-info">

                  <div class="category-icon">
                    <i class="bx bx-category"></i>
                  </div>

                  <div>
                    <strong>
                      {{ category.cat_name }}
                    </strong>

                    <small>
                      #{{ category.cat_id }}
                    </small>
                  </div>

                </div>
              </td>

              <!-- Type -->
              <td>
                <span
                  v-if="category.cat_type"
                  class="type-badge"
                >
                  {{ category.cat_type }}
                </span>

                <span
                  v-else
                  class="muted"
                >
                  -
                </span>
              </td>

              <!-- Meta Title -->
              <td>
                <div class="meta-title">
                  {{ category.cat_meta_title || '-' }}
                </div>
              </td>

              <!-- Keywords -->
              <td>
                <div class="keywords">
                  {{ category.cat_meta_keyword || '-' }}
                </div>
              </td>

              <!-- Created -->
              <td>
                {{ formatDate(category.cat_datetime) }}
              </td>

              <!-- Actions -->
              <td>

                <div class="actions">

                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewCategory(category)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editCategory(category)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(category)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>

              </td>

            </tr>

            <!-- Empty -->
            <tr v-if="categories.length === 0">

              <td colspan="6">

                <div class="empty-state">

                  <i class="bx bx-category"></i>

                  <h4>No categories found</h4>

                  <p>
                    Try changing your search or filter.
                  </p>

                </div>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

      <!-- Pagination -->
      <div
        v-if="filteredCategories.length > 0"
        class="pagination"
      >

        <span>
          Showing
          {{ startIndex + 1 }}
          -
          {{ Math.min(endIndex, filteredCategories.length) }}
          of
          {{ filteredCategories.length }}
        </span>

        <div class="pagination-buttons">

          <button
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>

          <button
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >

      <div class="modal">

        <div class="modal-header">

          <div>
            <h3>
              {{ isEditing ? 'Edit Category' : 'Add Category' }}
            </h3>

            <p>
              {{
                isEditing
                  ? 'Update category information.'
                  : 'Create a new category.'
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div class="modal-body">

          <div class="form-grid">

            <!-- Category Name -->
            <div class="form-group full">

              <label>
                Category Name <span>*</span>
              </label>

              <input
                v-model="form.cat_name"
                type="text"
                placeholder="Enter category name"
              />

            </div>

            <!-- Category Type -->
            <div class="form-group">

              <label>Category Type</label>

              <input
                v-model="form.cat_type"
                type="text"
                placeholder="e.g. course, blog, project"
              />

            </div>

            <!-- Meta Title -->
            <div class="form-group">

              <label>Meta Title</label>

              <input
                v-model="form.cat_meta_title"
                type="text"
                placeholder="Enter SEO meta title"
              />

            </div>

            <!-- Meta Keywords -->
            <div class="form-group full">

              <label>Meta Keywords</label>

              <textarea
                v-model="form.cat_meta_keyword"
                rows="4"
                placeholder="Enter SEO keywords"
              ></textarea>

            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeFormModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="saveCategory"
          >
            <i class="bx bx-save"></i>

            {{
              isEditing
                ? 'Update Category'
                : 'Create Category'
            }}
          </button>

        </div>

      </div>

    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="showViewModal = false"
    >

      <div class="modal view-modal">

        <div class="modal-header">

          <div>
            <h3>Category Details</h3>
            <p>View category information.</p>
          </div>

          <button
            class="close-btn"
            @click="showViewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div
          v-if="selectedCategory"
          class="modal-body"
        >

          <div class="category-preview">

            <div class="big-category-icon">
              <i class="bx bx-category"></i>
            </div>

            <div>

              <h2>
                {{ selectedCategory.cat_name }}
              </h2>

              <p>
                Category #{{ selectedCategory.cat_id }}

                <span>•</span>

                {{
                  selectedCategory.cat_type || 'General'
                }}
              </p>

            </div>

          </div>

          <div class="detail-grid">

            <div class="detail-item">

              <label>Category ID</label>

              <strong>
                #{{ selectedCategory.cat_id }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Category Type</label>

              <strong>
                {{
                  selectedCategory.cat_type || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Meta Title</label>

              <strong>
                {{
                  selectedCategory.cat_meta_title || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Created</label>

              <strong>
                {{
                  formatDate(
                    selectedCategory.cat_datetime
                  )
                }}
              </strong>

            </div>

          </div>

          <div class="content-section">

            <label>Meta Keywords</label>

            <div class="content-box">

              {{
                selectedCategory.cat_meta_keyword ||
                'No meta keywords available.'
              }}

            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="showViewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="editCategory(selectedCategory)"
          >
            <i class="bx bx-edit"></i>
            Edit Category
          </button>

        </div>

      </div>

    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="showDeleteModal = false"
    >

      <div class="delete-modal">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>Delete Category?</h3>

        <p>

          Are you sure you want to delete

          <strong>
            {{ selectedCategory?.cat_name }}
          </strong>?

          This action cannot be undone.

        </p>

        <div class="delete-actions">

          <button
            class="secondary-btn"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="deleteCategory"
          >
            Delete Category
          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getCategoriesApi,
  getCategoryApi,
  createCategoryApi,
  updateCategoryApi,
  deleteCategoryApi,
  getCategoryStatsApi,
} from "@/apis/categoryapis";

export default {
  name: "CategoriesScreen",

  data() {
    return {
      // ========================================
      // FILTERS
      // ========================================

      search: "",
      selectedType: "",

      categoryTypes: [
        "course",
        "creator",
        "content",
        "project",
        "blog",
      ],

      // ========================================
      // PAGINATION
      // ========================================

      currentPage: 1,
      perPage: 10,

      totalCategories: 0,
      totalPagesCount: 1,

      // ========================================
      // DATA
      // ========================================

      categories: [],

      // ========================================
      // STATS
      // ========================================

      stats: {
        totalCategories: 0,
        courseCategories: 0,
        creatorCategories: 0,
        contentCategories: 0,
      },

      // ========================================
      // LOADING
      // ========================================

      loading: false,
      saving: false,
      deleting: false,

      // ========================================
      // MODALS
      // ========================================

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,
      selectedCategory: null,

      // ========================================
      // FORM
      // ========================================

      form: {
        cat_name: "",
        cat_meta_title: "",
        cat_meta_keyword: "",
        cat_type: "course",
      },
    };
  },

  // ========================================
  // COMPUTED
  // ========================================

  computed: {
    /*
     * API already handles:
     * - search
     * - type filter
     * - pagination
     *
     * Therefore no local filtering/pagination.
     */

    filteredCategories() {
      return this.categories;
    },

    startIndex() {
      if (!this.totalCategories) {
        return 0;
      }

      return (
        (this.currentPage - 1) *
        this.perPage
      );
    },

    endIndex() {
      return (
        this.startIndex +
        this.categories.length
      );
    },

    /*
     * Template uses totalPages.
     * Keep it as an alias for totalPagesCount.
     */

    totalPages() {
      return this.totalPagesCount;
    },
  },

  // ========================================
  // WATCH
  // ========================================

  watch: {
    search() {
      this.currentPage = 1;
      this.loadCategories();
    },

    selectedType() {
      this.currentPage = 1;
      this.loadCategories();
    },
  },

  // ========================================
  // MOUNTED
  // ========================================

  mounted() {
    this.loadCategories();
    this.loadStats();
  },

  // ========================================
  // METHODS
  // ========================================

  methods: {
    // ========================================
    // LOAD CATEGORY LIST
    // ========================================

    async loadCategories() {
      this.loading = true;

      try {
        const res = await getCategoriesApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          cat_type: this.selectedType,
        });

        if (res?.status) {
          this.categories =
            Array.isArray(res.categories)
              ? res.categories
              : [];

          this.totalCategories =
            Number(res.total || 0);

          this.totalPagesCount = Math.max(
            Number(res.totalPages || 1),
            1
          );

          this.currentPage = Math.min(
            Math.max(
              Number(res.page || 1),
              1
            ),
            this.totalPagesCount
          );
        } else {
          this.categories = [];
          this.totalCategories = 0;
          this.totalPagesCount = 1;
        }
      } catch (error) {
        console.error(
          "Load Categories Error:",
          error
        );

        this.categories = [];
        this.totalCategories = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    // ========================================
    // LOAD STATS
    // ========================================

    async loadStats() {
      try {
        const res =
          await getCategoryStatsApi();

        if (res?.status) {
          /*
           * Support both:
           *
           * {
           *   status: true,
           *   stats: {...}
           * }
           *
           * and:
           *
           * {
           *   status: true,
           *   totalCategories: ...
           * }
           */

          if (res.stats) {
            this.stats = {
              totalCategories:
                Number(
                  res.stats.totalCategories || 0
                ),

              courseCategories:
                Number(
                  res.stats.courseCategories || 0
                ),

              creatorCategories:
                Number(
                  res.stats.creatorCategories || 0
                ),

              contentCategories:
                Number(
                  res.stats.contentCategories || 0
                ),
            };
          } else {
            this.stats = {
              totalCategories:
                Number(
                  res.totalCategories || 0
                ),

              courseCategories:
                Number(
                  res.courseCategories || 0
                ),

              creatorCategories:
                Number(
                  res.creatorCategories || 0
                ),

              contentCategories:
                Number(
                  res.contentCategories || 0
                ),
            };
          }
        }
      } catch (error) {
        console.error(
          "Load Category Stats Error:",
          error
        );
      }
    },

    // ========================================
    // RESET FILTER
    // ========================================

    resetFilters() {
      /*
       * Watchers will call loadCategories().
       * Do NOT call it again here.
       */

      this.search = "";
      this.selectedType = "";
      this.currentPage = 1;
    },

    // ========================================
    // TYPE BADGE
    // ========================================

    getTypeColor(type) {
      switch (type) {
        case "course":
          return "bg-blue-100 text-blue-700";

        case "creator":
          return "bg-green-100 text-green-700";

        case "content":
          return "bg-purple-100 text-purple-700";

        case "project":
          return "bg-orange-100 text-orange-700";

        case "blog":
          return "bg-pink-100 text-pink-700";

        default:
          return "bg-gray-100 text-gray-700";
      }
    },

    // ========================================
    // FORMAT TYPE
    // ========================================

    formatType(type) {
      if (!type) {
        return "-";
      }

      return (
        type.charAt(0).toUpperCase() +
        type.slice(1)
      );
    },

    // ========================================
    // FORMAT DATE
    // ========================================

    formatDate(date) {
      if (!date) {
        return "-";
      }

      const d = new Date(date);

      if (isNaN(d.getTime())) {
        return date;
      }

      return d.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },

    // ========================================
    // RESET FORM
    // ========================================

    resetForm() {
      this.form = {
        cat_name: "",
        cat_meta_title: "",
        cat_meta_keyword: "",
        cat_type: "course",
      };
    },

    // ========================================
    // ADD
    // ========================================

    openAddModal() {
      this.resetForm();

      this.selectedCategory = null;
      this.isEditing = false;
      this.showFormModal = true;
    },

    // ========================================
    // CLOSE FORM
    // ========================================

    closeFormModal() {
      this.showFormModal = false;

      this.resetForm();

      this.selectedCategory = null;
      this.isEditing = false;
    },

    // ========================================
    // VIEW
    // ========================================

    async viewCategory(category) {
      if (!category?.cat_id) {
        return;
      }

      try {
        const res =
          await getCategoryApi(
            category.cat_id
          );

        if (res?.status) {
          this.selectedCategory =
            res.category;

          this.showViewModal = true;
        } else {
          alert(
            res?.success ||
              "Unable to load category."
          );
        }
      } catch (error) {
        console.error(
          "View Category Error:",
          error
        );

        alert(
          "Unable to load category."
        );
      }
    },

    // ========================================
    // CLOSE VIEW
    // ========================================

    closeViewModal() {
      this.showViewModal = false;
      this.selectedCategory = null;
    },

    // ========================================
    // EDIT
    // ========================================

    async editCategory(category) {
      if (!category?.cat_id) {
        return;
      }

      try {
        const res =
          await getCategoryApi(
            category.cat_id
          );

        if (res?.status) {
          this.selectedCategory =
            res.category;

          this.isEditing = true;

          this.form = {
            cat_id:
              res.category.cat_id,

            cat_name:
              res.category.cat_name || "",

            cat_meta_title:
              res.category.cat_meta_title || "",

            cat_meta_keyword:
              res.category.cat_meta_keyword || "",

            cat_type:
              res.category.cat_type ||
              "course",
          };

          this.showViewModal = false;
          this.showFormModal = true;
        } else {
          alert(
            res?.success ||
              "Unable to load category."
          );
        }
      } catch (error) {
        console.error(
          "Edit Category Error:",
          error
        );

        alert(
          "Unable to load category."
        );
      }
    },

    // ========================================
    // SAVE
    // ========================================

    async saveCategory() {
      if (
        !this.form.cat_name ||
        !this.form.cat_name.trim()
      ) {
        alert(
          "Category Name is required."
        );

        return;
      }

      this.saving = true;

      try {
        let res;

        if (this.isEditing) {
          res =
            await updateCategoryApi(
              this.form
            );
        } else {
          res =
            await createCategoryApi(
              this.form
            );
        }

        if (res?.status) {
          alert(
            res.success ||
              (
                this.isEditing
                  ? "Category updated successfully."
                  : "Category created successfully."
              )
          );

          this.closeFormModal();

          await this.loadCategories();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Category save failed."
          );
        }
      } catch (error) {
        console.error(
          "Save Category Error:",
          error
        );

        alert(
          "Unable to save category."
        );
      } finally {
        this.saving = false;
      }
    },

    // ========================================
    // OPEN DELETE
    // ========================================

    confirmDelete(category) {
      if (!category?.cat_id) {
        return;
      }

      this.selectedCategory = category;
      this.showDeleteModal = true;
    },

    // ========================================
    // CLOSE DELETE
    // ========================================

    closeDeleteModal() {
      this.selectedCategory = null;
      this.showDeleteModal = false;
    },

    // ========================================
    // DELETE
    // ========================================

    async deleteCategory() {
      if (
        !this.selectedCategory?.cat_id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const res =
          await deleteCategoryApi(
            this.selectedCategory.cat_id
          );

        if (res?.status) {
          alert(
            res.success ||
              "Category deleted successfully."
          );

          this.closeDeleteModal();

          /*
           * If the last record on the current
           * page was deleted, go to previous page.
           */

          if (
            this.categories.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadCategories();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Category deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Category Error:",
          error
        );

        alert(
          "Unable to delete category."
        );
      } finally {
        this.deleting = false;
      }
    },

    // ========================================
    // PAGINATION
    // ========================================

    goToPage(page) {
      if (
        page < 1 ||
        page > this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;
      this.loadCategories();
    },

    previousPage() {
      if (this.currentPage <= 1) {
        return;
      }

      this.currentPage--;
      this.loadCategories();
    },

    nextPage() {
      if (
        this.currentPage >=
        this.totalPagesCount
      ) {
        return;
      }

      this.currentPage++;
      this.loadCategories();
    },
  },
};
</script>
<style scoped>
.categories-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.danger-btn,
.reset-btn {
  border: 0;
  border-radius: 9px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}

.secondary-btn:hover {
  background: #e2e8f0;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Filters */
.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 260px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-card select {
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  outline: none;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-card select {
  min-width: 180px;
  padding: 0 35px 0 12px;
}

.search-box input:focus,
.filter-card select:focus,
.form-group input:focus,
.form-group textarea:focus {
  border-color: #2563eb;
}

.reset-btn {
  height: 42px;
  background: #f1f5f9;
  color: #475569;
}

/* Table */
.table-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-top {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.table-top h3 {
  margin: 0 0 4px;
  font-size: 16px;
}

.table-top span {
  color: #64748b;
  font-size: 13px;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1050px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 13px 16px;
  text-align: left;
  white-space: nowrap;
}

td {
  padding: 15px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

tr:hover td {
  background: #fafcff;
}

.action-col {
  text-align: right;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 220px;
}

.category-icon {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.category-info strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
}

.category-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.type-badge {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  text-transform: capitalize;
}

.meta-title {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.keywords {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #64748b;
}

.muted {
  color: #94a3b8;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.icon-btn.view {
  color: #475569;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  padding: 55px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h4 {
  margin: 10px 0 5px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 13px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 34px;
  height: 34px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 7px;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 700px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 7px;
  background: #f1f5f9;
  color: #475569;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 17px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 12px;
  color: #334155;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.category-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e2e8f0;
}

.big-category-icon {
  width: 58px;
  height: 58px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  flex-shrink: 0;
}

.category-preview h2 {
  margin: 0 0 5px;
  font-size: 20px;
}

.category-preview p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.category-preview p span {
  margin: 0 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 22px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 9px;
}

.detail-item label {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  color: #0f172a;
  font-size: 13px;
  line-height: 1.5;
}

.content-section {
  margin-top: 22px;
}

.content-section > label {
  display: block;
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.content-box {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 14px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-word;
}

/* Delete */
.delete-modal {
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}

.delete-modal h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.delete-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

/* Responsive */
@media (max-width: 800px) {
  .filter-card {
    flex-wrap: wrap;
  }

  .search-box {
    flex-basis: 100%;
  }

  .filter-card select {
    flex: 1;
  }
}

@media (max-width: 650px) {
  .categories-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header .primary-btn {
    padding: 9px 12px;
  }

  .page-header .primary-btn i {
    display: none;
  }

  .filter-card {
    display: block;
  }

  .search-box {
    margin-bottom: 10px;
  }

  .filter-card select {
    width: 100%;
    margin-bottom: 10px;
  }

  .reset-btn {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>